import { notification } from "antd";
import { isEmpty, isNil, uniq } from "lodash";
import { saveBlob } from "utils/helpers/files";
import {
  buildConfigExport,
  configFileName,
  diffConfigAgainstAnchors,
  parseConfigImport,
} from "../../config-io";

const JSON_CONTENT_TYPE = "application/json;charset=utf-8";
const JSON_INDENT = 2;
const LIST_SEPARATOR = ", ";

const parsedConfig = (text) => {
  try {
    return { imported: parseConfigImport(text) };
  } catch (error) {
    return { error };
  }
};

const anchorWarning = ({ key, anchors, t }) =>
  isEmpty(anchors)
    ? []
    : [
        t(key, {
          count: anchors.length,
          anchors: anchors.join(LIST_SEPARATOR),
        }),
      ];

const configTransfer = ({ content, update, onImported, t }) => {
  const exportConfig = () =>
    saveBlob({
      content: JSON.stringify(
        buildConfigExport({
          name: content.name,
          anchors: uniq(content.anchors),
          anchorMappings: content.anchorMappings,
        }),
        null,
        JSON_INDENT,
      ),
      fileName: configFileName(content.name),
      contentType: JSON_CONTENT_TYPE,
    });

  const applyImport = (text) => {
    const { imported, error } = parsedConfig(text);
    if (!isNil(error)) {
      notification.error({
        message: t("CONFIG_IO.ERROR"),
        description: t(`CONFIG_IO.ERRORS.${error.code}`, {
          anchors: (error.anchors || []).join(LIST_SEPARATOR),
        }),
      });
      return;
    }
    update((current) => ({
      ...current,
      anchorMappings: imported.anchorMappings,
    }));
    onImported();
    const { uncovered, unknown } = diffConfigAgainstAnchors(
      imported.anchorMappings,
      content.anchors,
    );
    const warnings = [
      ...anchorWarning({ key: "CONFIG_IO.UNCOVERED", anchors: uncovered, t }),
      ...anchorWarning({ key: "CONFIG_IO.UNKNOWN", anchors: unknown, t }),
    ];
    const notify = isEmpty(warnings)
      ? notification.success
      : notification.warning;
    notify({
      message: t("CONFIG_IO.IMPORTED"),
      description: [
        t("CONFIG_IO.IMPORTED_DESCRIPTION", {
          count: Object.keys(imported.anchorMappings).length,
        }),
        ...warnings,
      ].join(" "),
    });
  };

  const importConfig = (event) => {
    const [selected] = event.target.files || [];
    event.target.value = "";
    if (isNil(selected)) return;
    const reader = new FileReader();
    reader.onload = () => applyImport(String(reader.result));
    reader.readAsText(selected);
  };

  return { exportConfig, importConfig };
};

export default configTransfer;
